import { computed, Ref, ref, watch } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useRoute } from 'vue-router';
import { useTimePeriodStore } from '@/common/stores/main-view-time-period';
import type { CurrentViewInfo, DbId, DbIdEnvValue, DbInfo } from '@/database/utils/overview.types';
import { hasDbSelect, isDatabaseOverview } from '@/database/utils/overview.utils';
import type { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { useUserEnvStore } from '@/common/stores/user-env';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { isNil } from 'lodash-es';
import { fetchPostgresqlDatabaseList } from '../utils/fetch/databaseFetch.utils';
import { InstanceRole } from '../utils/types';
import { useDatabaseUserEnv, useLastSelected } from '@/database/utils/overview.uses';
import { validateDatabaseOverviewRoute } from '../utils/path.util';

const DEFAULT_VIEW_INFO: CurrentViewInfo = {
  path: null,
  view: null,
  dbType: null,
  queryId: null,
};

const useGlobalTime = (currentViewInfo: Ref<CurrentViewInfo>) => {
  const timePeriodStore = useTimePeriodStore();
  const { timePeriodInfo } = storeToRefs(timePeriodStore);
  const { updateTimePeriod, initTimePeriod } = timePeriodStore;

  const viewTypeKey = computed<string>(() => {
    const { view, dbType, queryId } = currentViewInfo.value;
    if (!view || !dbType) {
      return '';
    }
    return `${view}-${dbType}-${queryId}`;
  });

  const selectedPeriodInfo = computed<TimePeriodInfo>(() => {
    return timePeriodInfo.value[viewTypeKey.value!] ?? {};
  });

  // 프레임 api 갱신 기준값
  const lastFetchedTime = ref<TimePeriodInfo['toTimeUtc']>(selectedPeriodInfo.value.toTimeUtc);

  const updatePeriodInfo = (info: TimePeriodInfo) => {
    if (viewTypeKey.value && info) {
      updateTimePeriod(viewTypeKey.value, info);
      lastFetchedTime.value = info.toTimeUtc;
    }
  };

  const initPeriodInfo = () => {
    initTimePeriod(viewTypeKey.value);
    lastFetchedTime.value = '';
  };

  return {
    timePeriodInfo,
    selectedPeriodInfo,
    lastFetchedTime,
    initPeriodInfo,
    updatePeriodInfo,
  };
};

const useDbInfo = () => {
  const dbInfo = ref<DbInfo[]>([]);
  const selectedDbIds = ref<DbId[] | null>(null);

  const instanceRole = ref<InstanceRole>();

  const envStore = useUserEnvStore();
  const envValue = ref<DbIdEnvValue>();

  const ENV_KEY = USER_ENV_KEYS.LASTSELECTED_database_postgresqlRtm_dbId;

  const initDbIds = () => {
    dbInfo.value = [];
    selectedDbIds.value = null;
    instanceRole.value = undefined;
  };

  const getLastSelectedDbIdsByInstance = (instanceId?: string): DbId[] | null => {
    if (!instanceId) {
      return null;
    }

    return envValue.value?.[instanceId] ?? null;
  };

  const fetchLastSelectedDbIds = async (instanceId?: string) => {
    if (!instanceId) {
      return null;
    }

    if (isNil(envValue.value)) {
      await envStore.fetchEnvDataByKeys([ENV_KEY]);
      envValue.value = envStore.getEnvValue<DbIdEnvValue>(ENV_KEY) ?? {};
    }

    return getLastSelectedDbIdsByInstance(instanceId) ?? null;
  };

  const setLastSelectedDbIds = async ({
    instanceId,
    dbIds: newSelectedDbIds,
  }: {
    instanceId?: string;
    dbIds: DbId[];
  }) => {
    if (!instanceId) {
      return;
    }

    const newEnvValue = { ...(envValue.value || {}), [instanceId]: newSelectedDbIds };
    await envStore.setEnvValue<DbIdEnvValue>([{ key: ENV_KEY, value: newEnvValue }]);
    envValue.value = newEnvValue;
  };

  const fetchDbIds = async (instanceId?: string) => {
    initDbIds();

    try {
      const data = await fetchPostgresqlDatabaseList(instanceId ? [instanceId] : []);

      const info = data[0] || {};
      dbInfo.value =
        info.databaseList?.map((item) => ({
          id: item.instanceId!,
          name: item.databaseName!,
        })) || [];
      instanceRole.value = info.instanceRole;

      const lastSelectedDbIds = await fetchLastSelectedDbIds(instanceId);
      if (!isNil(lastSelectedDbIds)) {
        selectedDbIds.value = lastSelectedDbIds.filter((id) =>
          dbInfo.value.find((db) => db.id === id),
        );
      } else {
        // user-env에 저장된 값이 없는 경우로 전체 선택
        selectedDbIds.value = dbInfo.value.map((item) => item.id);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getDbNameById = (id: DbId) => {
    return dbInfo.value.find((item) => item.id === id)?.name;
  };

  const selectedDatnames = computed<string[]>(() => {
    return (selectedDbIds.value || [])
      .map((item) => getDbNameById(item) ?? '')
      .filter((name) => name !== '');
  });

  return {
    dbInfo,
    selectedDbIds,
    selectedDatnames,
    instanceRole,
    setLastSelectedDbIds,
    fetchDbIds,
    getDbNameById,
    initDbIds,
  };
};

export const useDatabaseOverviewStore = defineStore('databaseOverview', () => {
  const route = useRoute();
  const currentViewInfo = ref<CurrentViewInfo>(DEFAULT_VIEW_INFO);

  const updateCurrentViewInfo = () => {
    const pathInfos = route?.path?.split('/');
    if (!pathInfos?.length || !isDatabaseOverview(pathInfos?.at(-1))) {
      currentViewInfo.value = DEFAULT_VIEW_INFO;
      return;
    }

    const [_, dbType, view] = pathInfos;
    const queryId = route?.query?.id;
    currentViewInfo.value = { view, dbType, queryId, path: route?.path } as CurrentViewInfo;
  };

  const globalTime = useGlobalTime(currentViewInfo);

  const {
    dbInfo,
    selectedDbIds,
    selectedDatnames,
    instanceRole,
    setLastSelectedDbIds,
    fetchDbIds,
    getDbNameById,
    initDbIds,
  } = useDbInfo();

  const { setUserEnv, getUserEnv } = useDatabaseUserEnv();

  const {
    initLastSelected,
    getLastSelectedOverviewInfo,
    getLastSelectedOverviewId,
    getLastInstances,
    getLastInstanceIds,
    getIdxFromLastInstanceIds,
    setLastOverviewInfo,
    fetchLastOverviewInfo,
  } = useLastSelected();

  const updateOverviewInfo = async () => {
    const { queryId } = currentViewInfo.value;
    const storedId = getLastSelectedOverviewId({
      dbType: currentViewInfo.value.dbType,
      viewType: currentViewInfo.value.view,
    });

    if (!!queryId && queryId !== storedId) {
      await validateDatabaseOverviewRoute(route);
    }
  };

  watch(
    () => route.fullPath,
    async (curr, prev) => {
      if (curr !== prev) {
        updateCurrentViewInfo();
        globalTime.initPeriodInfo();
        if (!hasDbSelect(currentViewInfo.value)) {
          initDbIds();
        }
        await updateOverviewInfo();
      }
    },
    { immediate: true },
  );

  return {
    currentViewInfo,
    ...globalTime,

    dbInfo,
    selectedDbIds,
    selectedDatnames,
    instanceRole,

    setLastSelectedDbIds,
    fetchDbIds,
    getDbNameById,

    setUserEnv,
    getUserEnv,

    initLastSelected,
    getLastSelectedOverviewInfo,
    getLastSelectedOverviewId,
    getLastInstances,
    getLastInstanceIds,
    getIdxFromLastInstanceIds,
    setLastOverviewInfo,
    fetchLastOverviewInfo,
  };
});
